// Service pour récupérer le taux de change EUR/MAD
export interface ExchangeRate {
  rate: number;
  lastUpdated: Date;
}

let cachedRate: ExchangeRate | null = null;
const CACHE_DURATION = 1000 * 60 * 60; // 1 heure

export const getEURtoMADRate = async (): Promise<number> => {
  // Vérifier si on a un taux en cache et s'il est encore valide
  if (cachedRate && (Date.now() - cachedRate.lastUpdated.getTime()) < CACHE_DURATION) {
    return cachedRate.rate;
  }

  try {
    // Utiliser l'API avec un timeout pour éviter les blocages
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // Timeout de 5 secondes
    
    try {
      const response = await fetch('https://api.exchangerate-api.com/v4/latest/EUR', {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error('Erreur lors de la récupération du taux de change');
      }

      const data = await response.json();
      const madRate = data.rates.MAD;

      if (!madRate) {
        throw new Error('Taux MAD non disponible');
      }

      // Mettre en cache le nouveau taux
      cachedRate = {
        rate: madRate,
        lastUpdated: new Date()
      };

      console.log(`Taux de change EUR/MAD mis à jour: ${madRate}`);
      return madRate;
    } catch (fetchError: any) {
      clearTimeout(timeoutId);
      if (fetchError.name === 'AbortError') {
        throw new Error('Timeout lors de la récupération du taux de change');
      }
      throw fetchError;
    }

  } catch (error) {
    console.error('Erreur lors de la récupération du taux de change:', error);
    
    // En cas d'erreur, utiliser un taux de fallback (approximatif)
    const fallbackRate = 10.5; // Taux approximatif EUR/MAD
    console.warn(`Utilisation du taux de fallback: ${fallbackRate}`);
    
    return fallbackRate;
  }
};

// Fonctions de conversion
export const convertEURtoMAD = (euros: number, rate: number): number => {
  if (!euros || !rate || euros <= 0 || rate <= 0) return 0;
  // Utiliser une précision plus élevée pour le calcul, puis arrondir
  const result = euros * rate;
  // Arrondir à 2 décimales en utilisant une méthode plus précise
  return Number(result.toFixed(2));
};

export const convertMADtoEUR = (mad: number, rate: number): number => {
  if (!mad || !rate || mad <= 0 || rate <= 0) return 0;
  // Utiliser une précision plus élevée pour le calcul, puis arrondir
  const result = mad / rate;
  // Arrondir à 2 décimales en utilisant une méthode plus précise
  return Number(result.toFixed(2));
};
